import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCourses } from '../utils/api';
import SmartImage from '../components/SmartImage';
import CourseStateBadge from '../components/CourseStateBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, User, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await getCourses('published');
      setCourses(response.data);
    } catch (error) {
      toast.error('Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Cargando cursos...</p>
      </div>
    );
  }

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="courses-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <h1 className="text-4xl font-heading font-bold text-primary mb-4">
            Catálogo de Cursos
          </h1>
          <p className="text-base text-muted-foreground">
            Explora nuestros cursos y comienza tu camino hacia el éxito profesional.
          </p>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No hay cursos disponibles en este momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, idx) => (
              <Card
                key={course.id}
                className="hover:shadow-lg transition-shadow group relative"
                data-testid={`course-card-${course.id}`}
              >
                {idx < 3 && (
                  <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent text-[#0b1220] text-xs font-bold shadow-md">
                    <Sparkles className="w-3 h-3" /> Nuevo
                  </div>
                )}
                {course.image_url && (
                  <div className="w-full h-48 overflow-hidden">
                    <SmartImage
                      src={course.image_url}
                      alt={course.title}
                      fallbackLabel={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="secondary">{course.status}</Badge>
                      <CourseStateBadge course={course} />
                    </div>
                    {course.hours && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{course.hours}h</span>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-xl font-heading group-hover:text-accent transition-colors">
                    {course.title}
                  </CardTitle>
                  {course.instructor_name && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="w-4 h-4" />
                      <span>{course.instructor_name}</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 line-clamp-3">
                    {course.description}
                  </CardDescription>
                  <Link to={`/courses/${course.id}`} data-testid={`view-course-${course.id}`}>
                    <Button className="w-full">Ver Detalles</Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;
